// intro.ts — the staged reveal choreography (progressive disclosure). Nothing shows at once:
//   0. DULY NOTED window is there → the welcome plays (~0.5s, in main.ts).
//   1. A beat after the welcome word finishes, START MASHING drops in from the top.
//   2. First fired word (a mash) → the under-the-hood rails slam up from the bottom, left→right.
//   3. Second fired word (another mash OR a side-widget button that fires) → after the DULY NOTED
//      word animation finishes, the type-a-word field STAMPS into the window (dramatic).
//
// The counter keys off `word:submitted`, which every firing control emits — mash buttons, the rail
// buttons (Fire/Randomize/Lively/Next/replay), and the typed field — but the auto-welcome does NOT
// (it bypasses submit). Only firing controls advance the sequence, exactly as Jason asked.

import { bus } from "../core/bus";
import { dropIn } from "./reveal";

// ── Tunable choreography timings (ms) ────────────────────────────────────────────────────────────
const MASH_IN_DELAY = 3300; // load → START MASHING drops in — a beat AFTER the welcome word finishes
const HINT_DELAY = 650; // panel lands → the onboarding scribble + arrow draw in a beat later
const RAILS_DELAY = 2300; // 1st mash → wait out the word animation (same as the stamp), then slam the rails up
const STAMP_DELAY = 2300; // 2nd fire → wait for the word animation to finish, then stamp the input in

export function startIntro(opts: {
  mashPanel: HTMLElement;
  revealRails: () => void; // wrapper reveals the rails AND (tutorial arm) their prompts, staggered
  stampInput: () => void; // wrapper stamps the field in AND (tutorial arm) shows the box prompt
  // Tutorial-arm (B) hooks — all optional; absent on the plain (A) arm. The SHOW of the rail/box
  // prompts rides inside revealRails / stampInput; here we only clear each as the player advances.
  showMashHint?: () => void; // phase-1 scribble — appears after the panel lands
  hideMashHint?: () => void; // …cleared the instant they mash
  hideRailHints?: () => void; // phase-2 rail prompts — cleared on the next fire
  hideBoxHint?: () => void; // phase-3 box prompt — cleared on the fire after that
}): void {
  dropIn(opts.mashPanel, MASH_IN_DELAY);
  if (opts.showMashHint) window.setTimeout(opts.showMashHint, MASH_IN_DELAY + HINT_DELAY);

  let n = 0;
  const off = bus.on("word:submitted", () => {
    n += 1;
    if (n === 1) {
      opts.hideMashHint?.(); // they mashed — clear the phase-1 hint
      window.setTimeout(opts.revealRails, RAILS_DELAY); // 1st mash → unlock the side rails (+ prompts)
    } else if (n === 2) {
      opts.hideRailHints?.(); // clear the rail prompts
      window.setTimeout(opts.stampInput, STAMP_DELAY); // 2nd fire → stamp the field in (+ box prompt)
    } else if (n === 3) {
      off();
      opts.hideBoxHint?.(); // 3rd fire → clear the last prompt; tutorial done
    }
  });
}
